// Pure scheduling logic for the satisfatorio filler playlist: which video
// plays when, cycling through the list until the composition ends.

export interface FillerPlaylistConfig {
  fillerUrl?: string;
  fillerUrls?: string[];
  /** Still used by Fofoca when no filler video has been chosen yet. */
  imageUrl?: string;
  fillerRandomize?: boolean;
  fillerSpeed?: number;
}

/**
 * The effective playlist for a config. The editor keeps `fillerUrl` synced to
 * `fillerUrls[0]`; when they disagree, something outside the editor (a
 * campaign-enforced layout merge) replaced `fillerUrl` without knowing about
 * the playlist — the enforced single video wins and the stale playlist is
 * dropped.
 */
export function resolveFillerPlaylist(config: FillerPlaylistConfig): string[] {
  const list = (config.fillerUrls ?? []).filter(Boolean);
  if (list.length === 0) {
    if (config.fillerUrl) return [config.fillerUrl];
    if (config.imageUrl) return [config.imageUrl];
    return [];
  }
  if (config.fillerUrl && list[0] !== config.fillerUrl) return [config.fillerUrl];
  return list;
}

export const AVG_FILLER_DURATION_SEC = 8;

/**
 * Resolve the playlist that should actually play. Templates can store
 * `fillerRandomize` without baking URLs; each video then draws a fresh
 * catalog shuffle sized to its duration.
 */
export function materializeFillerPlaylist(
  config: FillerPlaylistConfig,
  {
    pool,
    targetDurationSec,
    avgDurationSec = AVG_FILLER_DURATION_SEC,
    random = Math.random,
  }: {
    pool: string[];
    targetDurationSec: number;
    avgDurationSec?: number;
    random?: () => number;
  },
): string[] {
  if (config.fillerRandomize) {
    return pickRandomFillersToFill(pool, {
      targetDurationSec,
      avgDurationSec,
      fillerSpeed: config.fillerSpeed ?? 1,
      random,
    });
  }
  return resolveFillerPlaylist(config);
}

/** Drop preview-only URLs so a template does not freeze one shuffle. */
export function stripDeferredRandomFillers<T>(config: T): T {
  if (typeof config !== 'object' || config === null) return config;
  const bag = config as { fillerRandomize?: boolean; bottomFillerRandomize?: boolean };
  const stripTop = !!bag.fillerRandomize;
  const stripBottom = !!bag.bottomFillerRandomize;
  if (!stripTop && !stripBottom) return config;
  return {
    ...config,
    ...(stripTop ? { fillerUrl: '', fillerUrls: [] } : {}),
    ...(stripBottom ? { bottomFillerUrl: '', bottomFillerUrls: [] } : {}),
  };
}

/** Bake a fresh catalog shuffle onto a layout that asked to randomize. */
export function applyMaterializedFillers<T extends {
  kind: string;
  fillerRandomize?: boolean;
  fillerUrl?: string;
  fillerUrls?: string[];
  fillerSpeed?: number;
  bottomFillerRandomize?: boolean;
  bottomFillerUrl?: string;
  bottomFillerUrls?: string[];
}>(
  layout: T,
  {
    pool,
    targetDurationSec,
    random,
  }: {
    pool?: string[];
    targetDurationSec?: number;
    random?: () => number;
  },
): T {
  if (!pool?.length || targetDurationSec == null) return layout;
  let next = layout;
  if (layout.fillerRandomize) {
    const urls = materializeFillerPlaylist(
      {
        fillerUrl: layout.fillerUrl ?? '',
        fillerUrls: layout.fillerUrls,
        fillerRandomize: true,
        fillerSpeed: layout.fillerSpeed,
      },
      { pool, targetDurationSec, random },
    );
    next = { ...next, fillerUrls: urls, fillerUrl: urls[0] ?? '' };
  }
  if (layout.bottomFillerRandomize) {
    const urls = materializeFillerPlaylist(
      {
        fillerUrl: layout.bottomFillerUrl ?? '',
        fillerUrls: layout.bottomFillerUrls,
        fillerRandomize: true,
        fillerSpeed: layout.fillerSpeed,
      },
      { pool, targetDurationSec, random },
    );
    next = { ...next, bottomFillerUrls: urls, bottomFillerUrl: urls[0] ?? '' };
  }
  return next;
}

/** Clamp n into [lo, hi]. */
const clamp = (n: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, n));

/**
 * Move the item at `from` to `to`, returning a new array. Indices are clamped
 * to the valid range so an out-of-bounds drop lands at the nearest edge rather
 * than throwing.
 */
export function reorderPlaylist<T>(list: T[], from: number, to: number): T[] {
  const copy = [...list];
  if (copy.length === 0) return copy;
  const src = clamp(from, 0, copy.length - 1);
  const dst = clamp(to, 0, copy.length - 1);
  if (src === dst) return copy;
  const [moved] = copy.splice(src, 1);
  copy.splice(dst, 0, moved);
  return copy;
}

/**
 * Randomly pick videos from `pool` so their combined speed-adjusted length
 * covers `targetDurationSec`. Repeats after exhausting the catalog. The render
 * still loops the result, so this controls how much variety plays before the
 * sequence repeats. `avgDurationSec` is the assumed per-video length.
 */
export function pickRandomFillersToFill(
  pool: string[],
  {
    targetDurationSec,
    avgDurationSec,
    fillerSpeed,
    random = Math.random,
  }: {
    targetDurationSec: number;
    avgDurationSec: number;
    fillerSpeed: number;
    random?: () => number;
  },
): string[] {
  if (pool.length === 0) return [];
  const speed = Number.isFinite(fillerSpeed) && fillerSpeed > 0 ? fillerSpeed : 1;
  const perVideoSec = Math.max(0.001, avgDurationSec / speed);
  const needed = Math.max(
    1,
    Math.ceil((Number.isFinite(targetDurationSec) ? Math.max(0, targetDurationSec) : 0) / perVideoSec),
  );

  // Fisher–Yates over a copy, then cycle until the speed-adjusted length covers
  // the clip. Repeats are required when the catalog is shorter than the video.
  const shuffled = [...pool];
  for (let i = shuffled.length - 1; i > 0; i -= 1) {
    const j = Math.floor(random() * (i + 1));
    const k = clamp(j, 0, i);
    [shuffled[i], shuffled[k]] = [shuffled[k], shuffled[i]];
  }
  const picked: string[] = [];
  for (let i = 0; i < needed; i += 1) {
    picked.push(shuffled[i % shuffled.length]);
  }
  return picked;
}

/** Per-video fit mode. 'fill' (default) = cover + center; 'fit' = contain. */
export type FillerFit = 'fit' | 'fill';

/** Vertical anchor for a 'fill' (cover) filler: which slice stays visible when
 *  the video overflows the region. Default 'center'. */
export type FillerPosition = 'top' | 'center' | 'bottom';

/** CSS object-position Y keyword for a filler url (used for image fillers,
 *  where CSS object-position works). Default 'center'. */
export function fillerObjectPositionY(
  url: string,
  positions?: Record<string, FillerPosition>,
): FillerPosition {
  return positions?.[url] ?? 'center';
}

/**
 * Cover-crop geometry for anchoring a video top/bottom. `@remotion/media`'s
 * canvas <Video> bakes a centered cover crop and ignores CSS object-position,
 * so top/bottom anchoring needs an explicit box: the media scaled to cover the
 * region, horizontally centered, and vertically offset to the anchor. Returns
 * null for 'center' (keep the default full-cover path — byte-identical) or when
 * dimensions are unknown (degrade to center).
 */
export function fillerCoverBox({
  regionW,
  regionH,
  mediaW,
  mediaH,
  positionY,
}: {
  regionW: number;
  regionH: number;
  mediaW: number;
  mediaH: number;
  positionY: FillerPosition;
}): { width: number; height: number; top: number; left: number } | null {
  if (positionY === 'center') return null;
  if (
    !Number.isFinite(regionW) || !Number.isFinite(regionH) ||
    !Number.isFinite(mediaW) || !Number.isFinite(mediaH) ||
    regionW <= 0 || regionH <= 0 || mediaW <= 0 || mediaH <= 0
  ) {
    return null;
  }
  const scale = Math.max(regionW / mediaW, regionH / mediaH);
  const width = mediaW * scale;
  const height = mediaH * scale;
  const left = (regionW - width) / 2;
  const top = positionY === 'top' ? 0 : regionH - height;
  return { width, height, top, left };
}

const IMAGE_EXT_RE = /\.(jpe?g|png|webp|gif|avif|bmp)(?:[?#]|$)/i;

/** True when a filler url points at a still image (rendered as a static frame
 *  rather than a looping video). Detected by extension, query/fragment-safe. */
export function isImageFillerUrl(url: string): boolean {
  return IMAGE_EXT_RE.test(url || '');
}

/** CSS/Remotion objectFit for a filler url. Default 'cover' (fill, centered);
 *  only videos explicitly marked 'fit' letterbox with 'contain'. */
export function fillerObjectFit(
  url: string,
  fits?: Record<string, FillerFit>,
): 'cover' | 'contain' {
  return fits?.[url] === 'fit' ? 'contain' : 'cover';
}

/**
 * The fractional region the filler should occupy so it fills only the space
 * NOT covered by the floating clip cell (`clipRect`) — never full-bleed behind
 * the clip. Considers all four complementary strips (above / below / left /
 * right of the cell) and returns the one with the largest area, so it handles
 * both a top/bottom split (horizontal band) AND a side-by-side split (vertical
 * strip) regardless of composition orientation. `isLandscape` only breaks exact
 * ties, preferring the strip along the composition's dominant axis. With no
 * clipRect the caller uses the legacy split layout, so this returns the full
 * canvas.
 */
export function computeFillerRegion(
  clipRect: { x: number; y: number; w: number; h: number } | undefined | null,
  isLandscape: boolean,
): { x: number; y: number; w: number; h: number } {
  if (!clipRect) return { x: 0, y: 0, w: 1, h: 1 };
  const { x, y, w, h } = clipRect;
  const candidates = [
    { axis: 'v', x: 0, y: 0, w: 1, h: Math.max(0, y) },                 // above
    { axis: 'v', x: 0, y: y + h, w: 1, h: Math.max(0, 1 - (y + h)) },   // below
    { axis: 'h', x: 0, y: 0, w: Math.max(0, x), h: 1 },                 // left
    { axis: 'h', x: x + w, y: 0, w: Math.max(0, 1 - (x + w)), h: 1 },   // right
  ];
  // Prefer, on an exact area tie, the strip whose split axis matches the
  // composition: horizontal strips (left/right = 'h') in landscape, vertical
  // bands (above/below = 'v') in portrait.
  const preferAxis = isLandscape ? 'h' : 'v';
  const EPS = 1e-9;
  let best = candidates[0];
  for (const c of candidates) {
    const ca = c.w * c.h;
    const ba = best.w * best.h;
    if (ca > ba + EPS) best = c;
    else if (Math.abs(ca - ba) <= EPS && c.axis === preferAxis && best.axis !== preferAxis) best = c;
  }
  return { x: best.x, y: best.y, w: best.w, h: best.h };
}

export interface FillerScheduleSegment {
  /** Index into the playlist. */
  index: number;
  /** Output-timeline start frame. */
  from: number;
  durationInFrames: number;
}

/**
 * Lays playlist videos on the output timeline one after the other, looping the
 * whole list until the composition ends. Each video occupies
 * `duration / fillerSpeed` seconds of timeline (playbackRate compresses it).
 * Unknown durations fall back to `fallbackDurationSec` so a metadata failure
 * degrades to a fixed rotation instead of breaking the render.
 */
export function buildFillerSchedule({
  durationsSec,
  fillerSpeed,
  totalDurationInFrames,
  fps,
  fallbackDurationSec = 8,
}: {
  durationsSec: number[];
  fillerSpeed: number;
  totalDurationInFrames: number;
  fps: number;
  fallbackDurationSec?: number;
}): FillerScheduleSegment[] {
  if (durationsSec.length === 0 || totalDurationInFrames <= 0) return [];
  // A single video needs no schedule — it plays as one looping segment,
  // exactly like the pre-playlist behavior.
  if (durationsSec.length === 1) {
    return [{ index: 0, from: 0, durationInFrames: totalDurationInFrames }];
  }
  const speed = Number.isFinite(fillerSpeed) && fillerSpeed > 0 ? fillerSpeed : 1;
  const segmentFrames = durationsSec.map((d) => {
    const sec = Number.isFinite(d) && d > 0 ? d : fallbackDurationSec;
    // Minimum one frame so a degenerate duration can never stall the cycle.
    return Math.max(1, Math.round((sec / speed) * fps));
  });

  const segments: FillerScheduleSegment[] = [];
  let from = 0;
  let i = 0;
  while (from < totalDurationInFrames) {
    const index = i % segmentFrames.length;
    const durationInFrames = Math.min(segmentFrames[index], totalDurationInFrames - from);
    segments.push({ index, from, durationInFrames });
    from += durationInFrames;
    i += 1;
  }
  return segments;
}
